import { mapState } from 'pinia'

import { useConfigStore } from '../stores/config'
import { usePageStore } from '../stores/page'
import imgSrcSet from './img-src-set'
import deviceDetection from './device-detection'
import VideoMotionThumbnail from '../components/teaser/VideoMotionThumbnail.vue'
import Icon from '../components/shared/Icon.vue'
import TrackingPixel from '../components/tracking/TrackingPixel.vue'
import bskhCoverImage from './cover-image'

export default {
  components: {
    TrackingPixel,
    Icon,
    VideoMotionThumbnail
  },
  mixins: [imgSrcSet, deviceDetection, bskhCoverImage],
  props: {
    composedTeaser: {
      type: Object,
      default: () => {
        return {}
      }
    },
    metaData: {
      type: Object,
      default: () => {
        return {}
      }
    },
    includeDepth: {
      type: Number,
      default: 0
    },
    listOrderPosition: {
      type: Number,
      default: 0
    },
    isInlineTeaser: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fallbackPlaceholderSource: false,
      fallbackPlaceholderSourceWidth: 0,
      videoMotionThumbnailSelfDisabled: false, // for VideoMotionThumbnail.vue which might overwrite this value via $emit and triggers the fallback to <picture> in case of source does not exist or resolution is too low for parent container
      teaserMouseState: null
    }
  },
  computed: {
    // This store props is only used by a mixin
    ...mapState(useConfigStore, ['rsConfig']),
    ...mapState(usePageStore, ['isArticleOrArticleVersion']),
    imageLazyLoading () {
      return this.rsConfig.imageLazyLoading
    },
    getLoadingValue () {
      return (this.imageLazyLoading.isEnabled && this.listOrderPosition >= this.imageLazyLoading.aboveTheFoldParagraphCount.StructureTerm) ? 'lazy' : 'eager'
    },
    setBrandBadgeAlt () {
      return this.composedTeaser.ownerBrand ? this.composedTeaser.ownerBrand.charAt(0).toUpperCase() + this.composedTeaser.ownerBrand.substring(1) : ''
    },
    setLinkTarget () {
      return this.composedTeaser.target
        ? this.composedTeaser.target
        : this.composedTeaser.ownerBrand ? '_blank' : '_self'
    },
    showDownloadIcon () {
      return this.composedTeaser.hasDownload && this.rsConfig.shouldShowDownloadIconOnTeaser
    },
    showPlayIcon () {
      return (this.composedTeaser.articleType === 'video' || this.composedTeaser.hasAutoPlay) && this.metaData.teaserGroupType !== 'bx-teaser-container--info'
    },
    showGalleryIcon () {
      return this.composedTeaser.articleType === 'gallery'
    },
    setStyleClasses () {
      return {
        'bx-teaser': true,
        'bx-teaser--video': this.showPlayIcon,
        'bx-typo--teaser': true
      }
    },
    headline () {
      return this.composedTeaser.titleLong || this.composedTeaser.titleShort
    },
    imageAltText () {
      return this.composedTeaser.imageAltText || this.composedTeaser.titleLong || this.composedTeaser.titleShort
    },
    copytext () {
      return this.composedTeaser.textLong || this.composedTeaser.textShort
    },
    imageUrl () {
      return this.createImgSrc({
        image: this.composedTeaser,
        brand: this.composedTeaser.ownerBrand || this.metaData.brand,
        setName: this.metaData.teaserGroupType
      })
    },
    headlineTagType () {
      return this.isEditorialContext ? 'div' : 'h2'
    },
    setTabindex () {
      return this.metaData.teaserGroupType === 'bx-teaser-container--child-tags' ? '-1' : '0'
    },
    videoMotionThumbnailEnabled () {
      // disabled due problems with the video motion thumbnail on iPhones (fullscreen) and missing communication with editorial teams
      return false // this.composedTeaser.videoMediaId && !this.videoMotionThumbnailSelfDisabled
    },
    shouldAddDataNoSnippet () {
      return this.isEditorialContext ? '' : null
    },
    isEditorialContext () {
      return this.isArticleOrArticleVersion ||
             (this.composedTeaser.advertorial && !this.composedTeaser.hideAdvertorial) ||
             this.isInlineTeaser
    }
  },
  watch: {
    async composedTeaser () { // this is needed for the preview
      if (!import.meta.env.SSR && this.composedTeaser.overlayImageUrl && this.composedTeaser.overlayImageUrl.length > 0) {
        await this.$nextTick()
        this.setImageOverlayBackgroundSource(this.composedTeaser)
      }
    }
  },
  mounted () {
    if (!import.meta.env.SSR && this.composedTeaser.overlayImageUrl && this.composedTeaser.overlayImageUrl.length > 0) {
      this.setImageOverlayBackgroundSource(this.composedTeaser)
    }
  },
  methods: {
    setImageOverlayBackgroundSource (teaser) {
      const overlayDiv = document.getElementsByClassName('bx-js-image-overlay-' + teaser.imageId)
      if (overlayDiv) {
        const magazine = this.extractMagazineName(teaser.overlayImageUrl)
        const availableHeight = overlayDiv[0].clientHeight
        // base width (2:3 Ratio)
        const rawWidth = availableHeight * (2 / 3)
        // round up to next multiple of 20
        const optimalWidth = Math.ceil(rawWidth / 20) * 20
        overlayDiv[0].style.backgroundImage = `url(${this.coverImageUrl(magazine, optimalWidth)})`
      }
    },
    getImageOverlayStyleClass (teaser) {
      return 'bx-teaser__image-overlay bx-js-image-overlay-' + teaser.imageId
    },
    imgSrcSetArray (type) {
      let imgSrcSetArray = []
      if (this.includeDepth === 0) {
        imgSrcSetArray = this.createImgSrcSetArray(this.composedTeaser, this.composedTeaser.ownerBrand || this.metaData.brand, this.metaData.teaserGroupType, type)
      } else {
        imgSrcSetArray = this.createImgSrcSetArray(this.composedTeaser, this.composedTeaser.ownerBrand || this.metaData.brand, this.metaData.teaserGroupType + '--collection', type)
      }
      for (let i = 0; i < imgSrcSetArray.length; i++) {
        let imageWidth = imgSrcSetArray[i].src.match(/w=([0-9]+)/g)
        imageWidth = parseInt(imageWidth[0].substring(2))
        if (imageWidth > this.fallbackPlaceholderSourceWidth) {
          this.fallbackPlaceholderSourceWidth = imageWidth
          this.fallbackPlaceholderSource = imgSrcSetArray[i].src.replace('.webp', '.jpeg')
        }
      }
      return imgSrcSetArray
    },
    setMediaQuery (viewport) {
      return '(max-width: ' + viewport + 'px)'
    },
    brandBadgeSrc (type) {
      // isPreview property is set in TeaserGroupTeaser component in theGUI within the metaData property
      return (this.metaData.isPreview ? this.rsConfig.socialSettings.shareBaseUrl : '') + `/images/${this.composedTeaser.ownerBrand}/${type}-badge.png`
    }
  }
}
